import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import { Label } from '@shadcn/components/ui/label';
import { Switch } from '@shadcn/components/ui/switch';
import { Textarea } from '@shadcn/components/ui/textarea';
import { Save, Settings, X, History, RotateCcw, Copy } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import FlowCanvas from '../components/FlowBuilder/FlowCanvas';
import NodePalette from '../components/FlowBuilder/NodePalette';
import NodeConfigPanel from '../components/FlowBuilder/NodeConfigPanel';
import useFlowBuilder from '../hooks/useFlowBuilder';
import VersionHistory from '../components/FlowBuilder/VersionHistory';
import { NODE_TYPES, TRIGGER_TYPES, OPERATORS } from '../constants';
import cron from 'cron-validate';

const RuleBuilderPage = () => {
  const { ruleId } = useParams();
  const history = useHistory();
  const isEditMode = !!ruleId;
  const user = JSON.parse(localStorage.getItem('authUser') || '{}');
  const canView = Array.isArray(user?.permissions) && user.permissions.includes('ViewAutomations');

  const [showSettings, setShowSettings] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  const {
    nodes,
    edges,
    selectedNode,
    ruleName,
    ruleDescription,
    ruleSettings,
    eventTypes,
    actionTypes,
    fieldDefinitions,
    saving,
    loadedRule,
    setRuleName,
    setRuleDescription,
    setRuleSettings,
    setSelectedNode,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    updateNodeData,
    deleteNode,
    saveRule,
    saveAsNewRule,
    loadRule,
    loadVersion,
    restoreCurrentVersion,
    activeVersionId,
  } = useFlowBuilder();

  const updateSetting = (key, value) => {
    setRuleSettings(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (isEditMode) {
      loadRule(ruleId);
    }
  }, [ruleId, isEditMode, loadRule]);

  const validateFlow = () => {
    if (!ruleName.trim()) {
      toast.error('Please enter a rule name');
      return false;
    }
    const triggerNode = nodes.find(n => n.type === NODE_TYPES.TRIGGER);
    if (!triggerNode) {
      toast.error('Please add a trigger to your rule');
      return false;
    }
    if (triggerNode.data.triggerType === TRIGGER_TYPES.SCHEDULE) {
      const cronExpr = (triggerNode.data.schedule || '').replace(/['"]/g, '').trim();
      if (!cronExpr) {
        toast.error('Please enter a cron expression for the schedule trigger');
        return false;
      }
      const result = cron(cronExpr);
      if (!result.isValid()) {
        toast.error('Invalid cron expression. Example: 0 */5 * * * (every 5 minutes)');
        return false;
      }
    }
    if (triggerNode.data.triggerType === TRIGGER_TYPES.EVENT && !triggerNode.data.eventName?.trim()) {
      toast.error('Please select an event for the event trigger');
      return false;
    }
    if (triggerNode.data.triggerType === TRIGGER_TYPES.WEBHOOK) {
      const path = (triggerNode.data.webhookPath || '').trim();
      if (!path) {
        toast.error('Please enter a webhook path');
        return false;
      }
      if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(path)) {
        toast.error('Webhook path must be lowercase alphanumeric with hyphens (e.g. my-hook)');
        return false;
      }
    }
    // Cycle detection — walk edges from each node and flag if we revisit a node in the current path
    const hasCycle = () => {
      const visiting = new Set();
      const visited = new Set();
      const dfs = (nodeId) => {
        if (visiting.has(nodeId)) return true;
        if (visited.has(nodeId)) return false;
        visiting.add(nodeId);
        for (const edge of edges.filter(e => e.source === nodeId)) {
          if (dfs(edge.target)) return true;
        }
        visiting.delete(nodeId);
        visited.add(nodeId);
        return false;
      };
      return nodes.some(n => dfs(n.id));
    };
    if (hasCycle()) {
      toast.error('Flow contains a cycle — please remove circular connections');
      return false;
    }

    const actionNodes = nodes.filter(n => n.type === NODE_TYPES.ACTION);
    for (const action of actionNodes) {
      if (!action.data.actionType) {
        toast.error('All action nodes must have an action type selected');
        return false;
      }
    }
    const conditionNodes = nodes.filter(n => n.type === NODE_TYPES.CONDITION);
    for (const cn of conditionNodes) {
      const conditions = cn.data.conditionGroup?.conditions || [];
      const hasBlank = conditions.some(c => !c.field || !c.operator);
      if (hasBlank) {
        toast.error('All conditions must have a field and operator selected');
        return false;
      }
      for (const c of conditions) {
        if (c.operator === OPERATORS.REGEX_MATCH && c.value) {
          if (c.value.length > 500) {
            toast.error('Regex pattern is too long (max 500 characters)');
            return false;
          }
          try {
            new RegExp(c.value);
          } catch {
            toast.error(`Invalid regex pattern: ${c.value}`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFlow()) return;
    const success = await saveRule(ruleId);
    if (success) {
      history.push('/automation-engine');
    }
  };

  const handleSaveAsNew = async () => {
    if (!validateFlow()) return;
    const newRule = await saveAsNewRule();
    if (newRule) {
      history.push(`/automation-engine/rules/${newRule._id}/edit`);
    }
  };

  if (!canView) {
    return (
      <div className="page-content">
        <div className="text-center py-12 text-gray-500">You do not have permission to view this page.</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 pt-[70px]">
      {/* Three-column layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left - Node Palette */}
        <NodePalette actionTypes={actionTypes} />

        {/* Center - Flow Canvas */}
        <div className="flex-1 relative">
          {activeVersionId && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-300 rounded-lg shadow-md">
              <span className="text-sm text-amber-800 font-medium">Viewing previous version</span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={handleSaveAsNew}
                disabled={saving}
              >
                <Copy size={12} className="mr-1" />
                Duplicate
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={restoreCurrentVersion}
              >
                <RotateCcw size={12} className="mr-1" />
                Back to current
              </Button>
            </div>
          )}
          <FlowCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelectedNode(node)}
            onPaneClick={() => setSelectedNode(null)}
            addNode={addNode}
          />
          <div className="absolute top-3 left-3 z-10">
            <Input
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              placeholder="Untitled Workflow"
              className="w-72 font-semibold text-xl px-3 py-2 bg-transparent border-transparent hover:bg-white/80 hover:border-gray-300 hover:shadow-sm focus:bg-white focus:border-gray-400 focus:shadow-sm rounded-lg transition-all"
            />
          </div>
          <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
            {isEditMode && (
              <Button
                variant={showVersions ? 'secondary' : 'outline'}
                size="sm"
                className="shadow-md bg-white"
                onClick={() => {
                  setShowVersions(prev => !prev);
                  if (!showVersions) {
                    setShowSettings(false);
                    setSelectedNode(null);
                  }
                }}
              >
                <History size={16} />
              </Button>
            )}
            <Button
              variant={showSettings ? 'secondary' : 'outline'}
              size="sm"
              className="shadow-md bg-white"
              onClick={() => {
                setShowSettings(prev => !prev);
                if (!showSettings) {
                  setShowVersions(false);
                  setSelectedNode(null);
                }
              }}
            >
              <Settings size={16} />
            </Button>
            <Button className="shadow-md" onClick={handleSave} disabled={saving}>
              <Save size={16} className="mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Right - Config Panel, Settings Panel, or Version History */}
        {showVersions && isEditMode ? (
          <VersionHistory
            workflowId={loadedRule?.workflowId || ruleId}
            activeVersionId={activeVersionId}
            onSelectVersion={(version, index) => {
              if (index === 0 && activeVersionId) {
                restoreCurrentVersion();
              } else {
                loadVersion(version);
              }
            }}
            onClose={() => setShowVersions(false)}
          />
        ) : selectedNode ? (
          <NodeConfigPanel
            node={selectedNode}
            eventTypes={eventTypes}
            actionTypes={actionTypes}
            fields={fieldDefinitions}
            onUpdate={(data) => updateNodeData(selectedNode.id, data)}
            onDelete={() => {
              deleteNode(selectedNode.id);
              setSelectedNode(null);
            }}
            onClose={() => setSelectedNode(null)}
          />
        ) : showSettings && (
          <div style={{ flexBasis: '23%', maxWidth: '23%' }} className="shrink-0 grow-0 bg-white border-l overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium text-sm">Rule Settings</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
                <X size={14} />
              </Button>
            </div>
            <div className="p-4 space-y-5">
              <div>
                <Label>Description</Label>
                <Textarea
                  className="mt-1"
                  value={ruleDescription}
                  onChange={(e) => setRuleDescription(e.target.value)}
                  placeholder="What does this rule do?"
                  rows={3}
                />
              </div>

              <div>
                <Label>Max Retries</Label>
                <Input
                  type="number"
                  className="mt-1"
                  min={0}
                  value={ruleSettings.maxRetries}
                  onChange={(e) => updateSetting('maxRetries', parseInt(e.target.value) || 0)}
                />
              </div>

              <div>
                <Label>Retry Delay (ms)</Label>
                <Input
                  type="number"
                  className="mt-1"
                  min={0}
                  step={100}
                  value={ruleSettings.retryDelayMs}
                  onChange={(e) => updateSetting('retryDelayMs', parseInt(e.target.value) || 0)}
                />
              </div>

              <div>
                <Label>Timeout (ms)</Label>
                <Input
                  type="number"
                  className="mt-1"
                  min={0}
                  step={1000}
                  value={ruleSettings.timeoutMs}
                  onChange={(e) => updateSetting('timeoutMs', parseInt(e.target.value) || 0)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch
                  checked={ruleSettings.enabled}
                  onCheckedChange={(checked) => updateSetting('enabled', checked)}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default RuleBuilderPage;
